import { useCallback, useEffect, useMemo, useState } from 'react';
import { Facebook, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/store/hooks';
import { hasModuleFeature } from '@/features/auth/moduleFeatures';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { useConfirm } from '@/contexts/confirmContext';
import type { FacebookChannel } from '@workspace/types';
import { facebookAdminApi } from '../api/facebookApi';
import { FacebookChannelPanel } from '../components/FacebookChannelPanel';

type BotLabelMap = Record<string, string>;

type InstanceBotOption = {
  instanceBotId: string;
  label: string;
};

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

export function FacebookChannelsPage() {
  const currentUser = useAppSelector(selectCurrentUser);
  const { confirm } = useConfirm();
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const hasFacebook = hasModuleFeature(currentUser, 'facebook-integration');

  const [channels, setChannels] = useState<FacebookChannel[]>([]);
  const [botLabels, setBotLabels] = useState<BotLabelMap>({});
  const [instanceBotOptions, setInstanceBotOptions] = useState<InstanceBotOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBotId, setCreateBotId] = useState('');
  const [detailChannel, setDetailChannel] = useState<FacebookChannel | null>(null);

  const loadData = useCallback(async () => {
    if (!hasFacebook) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const tenantId = isSuperAdmin ? undefined : currentUser?.tenantId || undefined;
      const [channelList, instances] = await Promise.all([
        facebookAdminApi.listChannels(),
        chatbotAdminApi.listInstances(tenantId),
      ]);
      setChannels(channelList);

      const labels: BotLabelMap = {};
      const usedIds = new Set(channelList.map(channel => channel.instanceBotId));
      const available: InstanceBotOption[] = [];

      for (const instance of instances) {
        const bots = await chatbotAdminApi.listBots(instance.id);
        for (const bot of bots) {
          if (!bot.id) {
            continue;
          }
          const label = `${instance.name} / ${bot.name}`;
          labels[bot.id] = label;
          if (!usedIds.has(bot.id)) {
            available.push({ instanceBotId: bot.id, label });
          }
        }
      }

      setBotLabels(labels);
      setInstanceBotOptions(available);
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar canales Facebook');
    } finally {
      setLoading(false);
    }
  }, [hasFacebook, isSuperAdmin, currentUser?.tenantId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const sortedChannels = useMemo(
    () => [...channels].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [channels],
  );

  const createBotLabel = useMemo(() => {
    if (!createBotId) {
      return '';
    }
    return (
      instanceBotOptions.find(option => option.instanceBotId === createBotId)?.label ??
      botLabels[createBotId] ??
      createBotId
    );
  }, [createBotId, instanceBotOptions, botLabels]);

  const handleDelete = async (channel: FacebookChannel) => {
    const confirmed = await confirm({
      title: 'Eliminar canal',
      message: `¿Eliminar el canal Messenger (${channel.pageName ?? 'sin página'})?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) {
      return;
    }
    try {
      await facebookAdminApi.deleteChannel(channel.id);
      if (detailChannel?.id === channel.id) {
        setDetailChannel(null);
      }
      await loadData();
      toast.success('Canal eliminado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al eliminar canal');
    }
  };

  if (!hasFacebook) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Facebook className='h-5 w-5' />
            Facebook Messenger
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground'>
            La integración Facebook no está habilitada. Activá{' '}
            <code className='text-xs'>facebook-integration</code> en el tenant.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold flex items-center gap-2'>
            <Facebook className='h-6 w-6' />
            Canales Facebook Messenger
          </h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Conectá una Página de Facebook vía Evo Hub para recibir y responder DMs.
          </p>
        </div>
        <Button
          type='button'
          onClick={() => setCreateOpen(true)}
          disabled={instanceBotOptions.length === 0 && channels.length > 0}
        >
          <Plus className='h-4 w-4 mr-2' />
          Nuevo canal
        </Button>
      </div>

      <Card>
        <CardContent className='pt-6'>
          {loading ? (
            <p className='text-sm text-muted-foreground'>Cargando canales…</p>
          ) : sortedChannels.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No hay canales Messenger. Creá uno y conectalo con Evo Hub.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Página</TableHead>
                  <TableHead>Bot / instancia</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Actualizado</TableHead>
                  <TableHead className='text-right'>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedChannels.map(channel => (
                  <TableRow key={channel.id}>
                    <TableCell className='font-medium text-sm'>
                      {channel.pageName ?? '—'}
                    </TableCell>
                    <TableCell className='text-sm'>
                      {botLabels[channel.instanceBotId] ?? channel.instanceBotId}
                    </TableCell>
                    <TableCell>
                      <Badge variant={channel.status === 'ACTIVE' ? 'secondary' : 'outline'}>
                        {channel.status}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-sm text-muted-foreground'>
                      {formatDate(channel.updatedAt)}
                    </TableCell>
                    <TableCell className='text-right space-x-2'>
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => setDetailChannel(channel)}
                      >
                        Configurar
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        onClick={() => void handleDelete(channel)}
                      >
                        <Trash2 className='h-4 w-4 text-red-500' />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={open => !open && setCreateOpen(false)}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Nuevo canal Messenger</DialogTitle>
            <DialogDescription>Seleccioná el bot de instancia a vincular.</DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div>
              <Label>Bot de instancia</Label>
              <select
                className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                value={createBotId}
                onChange={e => setCreateBotId(e.target.value)}
              >
                <option value=''>Seleccionar…</option>
                {instanceBotOptions.map(option => (
                  <option key={option.instanceBotId} value={option.instanceBotId}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {createBotId ? (
              <FacebookChannelPanel
                instanceBotId={createBotId}
                botName={createBotLabel}
                onChannelChange={() => {
                  setCreateOpen(false);
                  void loadData();
                }}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detailChannel)} onOpenChange={open => !open && setDetailChannel(null)}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Configurar canal Messenger</DialogTitle>
            <DialogDescription>
              {detailChannel ? botLabels[detailChannel.instanceBotId] : ''}
            </DialogDescription>
          </DialogHeader>
          {detailChannel ? (
            <FacebookChannelPanel
              instanceBotId={detailChannel.instanceBotId}
              botName={botLabels[detailChannel.instanceBotId] ?? detailChannel.instanceBotId}
              onChannelChange={() => void loadData()}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
